/**
 * UpdateBar — the quiet mid-session "a new version is ready" affordance.
 *
 * Shown by AutoUpdater ONLY when an update is detected while the app is visible
 * mid-session (the user may be reading or editing). On a clean cold start /
 * arrival, or when the tab is hidden, AutoUpdater applies the update silently
 * and this bar never appears — so most users never see it.
 *
 * The bar is deliberately passive: it is non-modal, does not steal focus, and
 * never reloads on its own. The reload happens only when the user taps Reload.
 * That is what protects an active edit session — a CRDT draft is persisted, but
 * a forced reload mid-keystroke is still jarring, so the choice stays the
 * user's (fix-pwa-update-delivery, 2026-07-17).
 *
 * Instrument styling (design.md §2 / §4):
 *   - hairline top border (var(--line)) + warm surface, no shadow;
 *   - accent reserved for the primary action (the Reload button) — a signal
 *     surface per the one-accent doctrine (design.md §2);
 *   - Geist for the prose (not mono — this is a sentence, not data);
 *   - 200 ms ease slide-in, no spring, no bounce;
 *   - safe-area-aware bottom inset for iOS standalone.
 *
 * Copy (branding.md §5): "A new version is ready" — no exclamation, no banned
 * word, states what is true and offers the single action. "Reload" is the plain
 * verb for the action.
 */

import { useEffect, useState, type ReactElement } from "react";

export interface UpdateBarProps {
  /** Apply the pending update (flush caches, activate the waiting SW, reload). */
  onReload: () => void;
}

export function UpdateBar({ onReload }: UpdateBarProps): ReactElement {
  // Mount → enter transition (translateY). Quiet 200 ms ease, no spring.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="pwa-update-bar"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 70, // z-toast — above chrome, below nothing that must block it
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
        padding: "12px 16px",
        paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
        background: "var(--surface)",
        borderTop: "1px solid var(--line)",
        color: "var(--text)",
        fontSize: "15px",
        lineHeight: 1.4,
        transform: entered ? "translateY(0)" : "translateY(100%)",
        transition: "transform 200ms ease",
      }}
    >
      <span style={{ minWidth: 0 }}>A new version is ready</span>
      <button
        type="button"
        onClick={onReload}
        data-testid="pwa-update-reload"
        style={{
          flexShrink: 0,
          appearance: "none",
          border: "none",
          borderRadius: "8px",
          padding: "8px 16px",
          background: "var(--accent)",
          color: "var(--surface)",
          fontSize: "15px",
          fontWeight: 500,
          lineHeight: 1,
          cursor: "pointer",
          // No shadow. design.md §4.6 RESERVES the single permitted shadow
          // (`0 1px 2px rgba(0,0,0,0.04)`) for two named surfaces only — the
          // Share top-bar CTA and the mobile Spin-off bottom bar — and the
          // UpdateBar is neither. Default doctrine (§2 principle 2: hairlines
          // over shadows) governs here; the accent fill already carries the
          // primary-action signal (§2 principle 1).
        }}
      >
        Reload
      </button>
    </div>
  );
}
